import { Suspense, lazy, useMemo, useEffect, useState } from "react";
import { Routes, Route, useParams, Navigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider, keepPreviousData } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { PlatformGuard } from "@/components/guards/PlatformGuard";
import { VerificationRedirectHandler } from "@/components/auth/VerificationRedirectHandler";
import { CookieConsentBanner } from "@/components/legal/CookieConsentBanner";
import { getTenantStoreSubdomainOrPreview, getTenantRootDomain } from "@/lib/tenant/getSubdomain";
import { resolveCustomDomainSlug } from "@/lib/tenant/resolveCustomDomain";
import { StorefrontTenantProvider } from "@/contexts/StorefrontTenantContext";
import { MainAppRoutes } from "@/routes/MainAppRoutes";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { EditorTemplateValidationBridge } from "@/components/dashboard-v2/EditorTemplateValidationBridge";
import { GoogleAnalytics } from "@/components/analytics/GoogleAnalytics";

const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const OrderFailed = lazy(() => import("@/pages/OrderFailed"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// New block-based storefront — used for tenant subdomain access
const TenantStorefrontLayout = lazy(() =>
  import("@/storefront/layout/TenantStorefrontLayout").then((m) => ({
    default: m.TenantStorefrontLayout,
  }))
);
const RuntimePage = lazy(() => import("@/storefront-runtime/runtime/RuntimePage"));
const BuyerLoginPage = lazy(() => import("@/storefront/pages/BuyerLoginPage"));
const BuyerRegisterPage = lazy(() => import("@/storefront/pages/BuyerRegisterPage"));
const BuyerOrdersPage = lazy(() => import("@/storefront/pages/BuyerOrdersPage"));
const BuyerAccountPage = lazy(() => import("@/storefront/pages/BuyerAccountPage"));
const BuyerVerifyPage = lazy(() => import("@/storefront/pages/BuyerVerifyPage"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));

// Inline SVG spinner — zero dependency, no lucide-react in App shell critical path.
// lucide-react (vendor-icons, 129KB) still loads for the Landing page which uses
// many icons, but this avoids the import being the reason it's eagerly loaded.
function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

const LoadingPage = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <SpinnerIcon className="h-10 w-10 animate-spin text-primary" />
      <p className="text-sm font-medium text-muted-foreground">Chargement de FastSell...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dashboard tabs should render from cache first, then refresh explicitly.
      staleTime: 60_000,
      gcTime: 300_000,
      placeholderData: keepPreviousData,
      // One retry on failure; avoids hammering the BFF on transient errors.
      retry: 1,
      // Window focus refetches are already handled by StoreContext's visibilitychange
      // listener for critical store data. Disabling the global default prevents
      // React Query from firing a full refetch of every cached query on tab focus.
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * On subdomains (e.g. ministore.usefastsell.com), links generated by
 * resolveRefStorefrontNavLink still include the /ref/:storeSlug/ prefix.
 * This component strips it and redirects to the clean tenant path.
 * e.g. /ref/ministore/product/eciteur → /product/eciteur
 */
function RefPrefixRedirect() {
  const { '*': rest = '' } = useParams<{ '*': string }>()
  return <Navigate to={`/${rest}`} replace />
}

/** Storefront routes at root path when host is {slug}.tenantRoot (e.g. reed.usefastsell.com). */
function TenantStoreRoutes({ slug }: { slug: string }) {
  return (
    <StorefrontTenantProvider slug={slug}>
      <Suspense fallback={<LoadingPage />}>
        <Routes>
          {/* Block-based storefront shell — provides ReferenceStoreProvider */}
          <Route element={<TenantStorefrontLayout slug={slug} />}>
            <Route index element={<RuntimePage />} />
            <Route path="shop" element={<RuntimePage />} />
            <Route path="product/:productSlug" element={<RuntimePage />} />
            <Route path="category/:categorySlug" element={<RuntimePage />} />
            <Route path="cart" element={<RuntimePage />} />
            <Route path="checkout" element={<RuntimePage />} />
            <Route path="search" element={<RuntimePage />} />
            <Route path="about" element={<RuntimePage />} />
            <Route path="contact" element={<RuntimePage />} />
            <Route path="faq" element={<RuntimePage />} />
            <Route path="policy/shipping" element={<RuntimePage />} />
            <Route path="policy/returns" element={<RuntimePage />} />
            <Route path="account" element={<BuyerAccountPage />} />
            <Route path="account/login" element={<BuyerLoginPage />} />
            <Route path="account/register" element={<BuyerRegisterPage />} />
            <Route path="account/verify" element={<BuyerVerifyPage />} />
            <Route path="account/orders" element={<BuyerOrdersPage />} />
          </Route>

          {/* Standalone pages (no storefront chrome needed) */}
          <Route path="track-order" element={<TrackOrder />} />
          <Route path="order-success/:orderId" element={<OrderSuccess />} />
          <Route path="order-failed/:orderId" element={<OrderFailed />} />
          <Route path="maintenance" element={<Maintenance />} />
          {/*
            Redirect /ref/:storeSlug/* → /* when accessed on a subdomain.
            Happens when a /ref/ link is shared and opened on the subdomain.
          */}
          <Route path="ref/:storeSlug/*" element={<RefPrefixRedirect />} />
          <Route path="ref/:storeSlug" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </StorefrontTenantProvider>
  );
}

function AppWithTenantRouting() {
  const location = useLocation();
  const tenantSlug = useMemo(
    () => getTenantStoreSubdomainOrPreview(new URLSearchParams(location.search)),
    [location.search],
  );

  // undefined = still resolving; null = not a custom domain; string = found slug
  const [customDomainSlug, setCustomDomainSlug] = useState<string | null | undefined>(
    tenantSlug ? null : undefined
  );

  useEffect(() => {
    if (tenantSlug) return;

    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    const root = getTenantRootDomain();
    const isKnownHost =
      !hostname ||
      hostname === "localhost" ||
      hostname.startsWith("127.") ||
      hostname === root ||
      hostname === `www.${root}` ||
      hostname.endsWith(`.${root}`) ||
      hostname.endsWith(".netlify.app");

    if (isKnownHost) {
      setCustomDomainSlug(null);
      return;
    }

    resolveCustomDomainSlug(hostname).then((slug) => {
      setCustomDomainSlug(slug ?? null);
    });
  }, [tenantSlug]);


  const effectiveSlug = tenantSlug ?? (customDomainSlug ?? null);

  // Show loader while we're resolving the custom domain lookup
  if (customDomainSlug === undefined && !tenantSlug) {
    return <LoadingPage />;
  }

  return (
    <Suspense fallback={<LoadingPage />}>
      {effectiveSlug ? (
        <TenantStoreRoutes slug={effectiveSlug} />
      ) : (
        <ThemeProvider>
          <MainAppRoutes />
        </ThemeProvider>
      )}
    </Suspense>
  );
}

// TooltipProvider is NOT in the App root. Moving it here would force
// @radix-ui/react-tooltip (part of vendor-radix-ui, ~87KB) to load on every
// public page (landing, login, signup) even though tooltips are only used in
// the dashboard. It is now mounted inside DashboardLayoutV2 which is lazy.
const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Sonner />
        <EditorTemplateValidationBridge />
        <BrowserRouter>
          <GoogleAnalytics />
          <VerificationRedirectHandler />
          <CookieConsentBanner />
          <PlatformGuard>
            <AppWithTenantRouting />
          </PlatformGuard>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
